'use client';

import {
  HumanVerificationChallenge,
  HumanVerificationChallengeHandle,
} from '@/components/human-verification-challenge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  selectStorePayload,
  selectSubcribePartnerStatus,
  subscribePartnerStore,
  SubscribePartnerStoreState,
  useSubscribePartnerStore,
} from '@/stores/subscribePartnerStore';
import { scrollToElement } from '@/util/helpers';
import { SubmitStatuses } from '@/util/storeUtilities';
import { BarChart, Check, DollarSign, Shield, Users } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { equalityFn } from 'wired-store';

const marketStats = [
  {
    number: '55-65%',
    label: "of car owners don't purchase VSCs at time of vehicle purchase",
  },
  {
    number: '$40B+',
    label: 'Vehicle Service Contract market size',
  },
  {
    number: '12+ years',
    label: 'Average vehicle age in the U.S.',
  },
];

const benefits = [
  {
    icon: <DollarSign className="w-12 h-12 text-[#00B67A]" />,
    title: 'Access Untapped Market',
    description:
      'Increase revenue and expand your customer base effortlessly through our platform without the overhead.',
  },
  {
    icon: <Shield className="w-12 h-12 text-[#00B67A]" />,
    title: 'Trusted Platform',
    description:
      'Partner with a transparent, customer-focused platform that prioritizes clear pricing and comprehensive coverage options.',
  },
  {
    icon: <Users className="w-12 h-12 text-[#00B67A]" />,
    title: 'Growing Customer Base',
    description: 'Access a large pool of vehicle owners actively seeking protection plans through our platform.',
  },
  {
    icon: <BarChart className="w-12 h-12 text-[#00B67A]" />,
    title: 'Data Insights',
    description:
      'Get valuable insights into customer preferences and market trends through our advanced analytics dashboard.',
  },
];

const features = [
  'Easy integration with existing systems',
  'Dedicated partner support team',
  'Real-time analytics and reporting',
  'Flexible commission structure',
  'Marketing and promotional support',
  'Training and onboarding resources',
];

export function PartnerComponent() {
  const humanVerificationRef = useRef<HumanVerificationChallengeHandle>(null);
  const { payload, status } = useSubscribePartnerStore(
    (state) => ({
      payload: selectStorePayload(state),
      status: selectSubcribePartnerStatus(state),
    }),
    equalityFn,
  );
  const disableSubmitButton = status === SubmitStatuses.SUBMITTING;

  // where you render the ToastContainer:
  useEffect(() => {
    const subscriptions = [
      subscribePartnerStore.subscribe(
        (message: SubscribePartnerStoreState['notifySuccess']) => {
          if (message) {
            toast.success(message);
            subscribePartnerStore.clearNotifySuccess();
          }
        },
        (state): SubscribePartnerStoreState['notifySuccess'] => state.notifySuccess,
      ),
      subscribePartnerStore.subscribe(
        (message: SubscribePartnerStoreState['notifyFailure']) => {
          if (message) {
            toast.error(message);
            subscribePartnerStore.clearNotifyFailure();
          }
        },
        (state): SubscribePartnerStoreState['notifyFailure'] => state.notifyFailure,
      ),
    ];
    return () => {
      for (const unsubscribe of subscriptions) {
        unsubscribe();
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#00B67A] text-white relative overflow-hidden">
        <div className="container mx-auto px-4 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative z-10">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Grow Your Business with WarrantyPilot</h1>
              <p className="text-xl mb-8">
                Join our network of successful partners and tap into the $40B+ Vehicle Service Contract market.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {marketStats.map((stat, index) => (
                  <div key={index} className="bg-white/10 p-4 rounded-lg backdrop-blur-sm">
                    <div className="text-2xl font-bold">{stat.number}</div>
                    <div className="text-sm text-white/80">{stat.label}</div>
                  </div>
                ))}
              </div>
              <Button
                size="lg"
                variant="secondary"
                className="text-[#00B67A] hover:text-white bg-white hover:bg-gray-900"
                onClick={() => {
                  scrollToElement('signupform');
                }}
              >
                Become a Partner Today
              </Button>
            </div>
            <div className="relative">
              <Image
                src="/images/partner.jpg"
                alt="Partner Success"
                width={400}
                height={600}
                className="rounded-lg shadow-2xl mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Why Partner With Us?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Join a platform that's revolutionizing the vehicle protection industry through transparency and
              technology.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="p-6">
                <div className="mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-gray-50 py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Everything You Need to Succeed</h2>
              <p className="text-xl text-gray-600 mb-8">
                We provide all the tools and support you need to effectively sell vehicle protection plans and grow your
                business.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#00B67A]" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <Image
                src={'/images/woman-recieving-car-keys.png'}
                alt="Partner Dashboard Preview"
                width={600}
                height={400}
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section id="signupform" className="py-24">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Get Started Today</h2>
            <p className="text-xl text-gray-600">
              Join our growing network of successful partners and start earning additional revenue.
            </p>
          </div>

          <Card className="p-8">
            <div className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={payload.email}
                  onChange={(e) => {
                    subscribePartnerStore.setEmail(e.target.value);
                  }}
                  required
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    First Name
                  </label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={payload.firstName}
                    onChange={(e) => {
                      subscribePartnerStore.setFirstName(e.target.value);
                    }}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Last Name
                  </label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={payload.lastName}
                    onChange={(e) => {
                      subscribePartnerStore.setLastName(e.target.value);
                    }}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={payload.phone}
                    onChange={(e) => {
                      subscribePartnerStore.setPhone(e.target.value);
                    }}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                    Company
                  </label>
                  <Input
                    id="company"
                    name="company"
                    value={payload.company}
                    onChange={(e) => {
                      subscribePartnerStore.setCompany(e.target.value);
                    }}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="partnerType" className="block text-sm font-medium text-gray-700">
                  Partnership Type
                </label>
                <Select
                  value={payload.partnershipType}
                  onValueChange={(e) => {
                    subscribePartnerStore.setPartnershipType(e);
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select partnership type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Warranty Provider">Warranty Provider</SelectItem>
                    <SelectItem value="Dealership">Dealership</SelectItem>
                    <SelectItem value="Financial Institutions">Financial Institutions</SelectItem>
                    <SelectItem value="Auto & Repair Shops">Auto Shops & Repair Centers</SelectItem>
                    <SelectItem value="Website Integration">Website Integration</SelectItem>
                    <SelectItem value="Api Access">API Access</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={payload.message}
                  onChange={(e) => {
                    subscribePartnerStore.setMessage(e.target.value);
                  }}
                  rows={4}
                  className="mt-1"
                />
              </div>
              <HumanVerificationChallenge ref={humanVerificationRef} />
              <div>
                <Button
                  type="submit"
                  className="w-full bg-[#00B67A] hover:bg-[#008C5E]"
                  size="lg"
                  disabled={disableSubmitButton}
                  onClick={() => {
                    if (!humanVerificationRef.current?.verify()) {
                      return;
                    }

                    subscribePartnerStore.submit();
                  }}
                >
                  Submit Application
                </Button>
              </div>

              <p className="text-center text-sm text-gray-500">
                By submitting this form, you agree to our{' '}
                <Link
                  href="https://docs.google.com/document/d/1n6l_UrviP_IH3B8Fw2P0gNjcCMmTnhU0y1gx2df4VKk/edit?usp=sharing"
                  className="text-[#00B67A] hover:underline"
                >
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link
                  href="https://docs.google.com/document/d/1ECs_ufYtJHOmE7rXR9PMFcbNGAUBCZHwnBQDaaTotpE/edit?usp=sharing"
                  className="text-[#00B67A] hover:underline"
                >
                  Privacy Policy
                </Link>
              </p>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to grow your business?</h2>
          <p className="text-gray-600 mb-8">Our partner success team is here to help you get started.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* <Button size="lg" className="bg-[#00B67A] hover:bg-[#008C5E]">
              Apply Now
            </Button>
            <Button size="lg" variant="outline">
              Schedule a Demo
            </Button> */}
          </div>
        </div>
      </section>
    </div>
  );
}
